import { useState, useRef, useEffect } from 'react'
import { Globe, ChevronUp } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useChannelStore } from '@/store/channel'
import { getAvailableSwitcherLanguages } from '@/i18n/languages'
import { useChangeLanguage } from '@/hooks/useChangeLanguage'

export default function LanguageSwitcher() {
  const { t } = useTranslation()
  const availableLanguageCodes = useChannelStore((store) => store.availableLanguageCodes)
  const currentLanguageCode = useChannelStore((store) => store.currentLanguageCode)
  const changeLanguage = useChangeLanguage()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const options = getAvailableSwitcherLanguages(availableLanguageCodes)
  if (options.length === 0) return null

  const current = options.find((option) => option.vendureCode === currentLanguageCode) ?? options[0]

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      {isOpen && (
        <div className="absolute bottom-full left-0 mb-2 w-48 max-h-64 overflow-y-auto bg-background border border-secondary/10 rounded-xl shadow-lg z-20">
          {options.map((option) => (
            <button
              key={option.vendureCode}
              type="button"
              onClick={() => {
                changeLanguage(option.vendureCode)
                setIsOpen(false)
              }}
              className={`w-full text-left px-4 py-3 text-sm transition-colors cursor-pointer ${
                option.vendureCode === current.vendureCode
                  ? 'bg-primary/5 text-primary-text font-medium'
                  : 'text-foreground hover:bg-secondary/5'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        className="flex items-center gap-2 px-4 py-2.5 rounded-full border border-secondary/20 bg-background text-sm text-foreground hover:border-secondary/40 transition-colors cursor-pointer"
        title={t('languageSwitcher.label')}
        aria-label={t('languageSwitcher.label')}
      >
        <Globe className="w-4 h-4 text-secondary" />
        <span>{current.label}</span>
        <ChevronUp className={`w-4 h-4 text-secondary transition-transform ${isOpen ? '' : 'rotate-180'}`} />
      </button>
    </div>
  )
}
